import React, { useState, useCallback } from 'react'
import type { CustomBanner, PositionedBannerInput } from 'api/generated'
import classNames from 'classnames'
import { Button } from 'components/ui/button'
import { Input } from 'components/ui/input'
import { Loader } from 'lucide-react'
import Select, { MultiValue, SingleValue } from 'react-select'
import { toast } from 'sonner'
import { saveBanner } from 'store/banners/bannersSlice'
import { useAppDispatch, useAppSelector } from 'store/store'

type BannerableType = PositionedBannerInput['bannerable_type']

interface BannerableTypeOption {
  value: BannerableType
  label: string
}

// ADSENSE is accepted by the API but not offered here (no AdSense slot picker)
const BANNERABLE_TYPES: BannerableTypeOption[] = [
  { value: 'TENNISTALKER', label: 'TENNISTALKER' },
]

interface BannerOption {
  value: string
  label: string
}

interface BannerFormProps {
  className?: string
}

const BannerForm: React.FC<BannerFormProps> = ({ className }) => {
  const dispatch = useAppDispatch()
  const customBannersState = useAppSelector((state) => state.customBanners)

  const [slot, setSlot] = useState('')
  const [bannerableType, setBannerableType] = useState(BANNERABLE_TYPES[0])
  const [bannerableId, setBannerableId] = useState<number[]>([])

  const isValid = useCallback(() => {
    return slot && bannerableType && bannerableId.length > 0
  }, [slot, bannerableType, bannerableId])

  const handleSave = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault()
      if (isValid()) {
        dispatch(
          saveBanner({
            slot,
            bannerable_type: bannerableType.value,
            bannerable_id: bannerableId,
          })
        )
          .unwrap()
          .then(() => {
            toast.success('Banner inserito con successo')
            setSlot('')
            setBannerableType(BANNERABLE_TYPES[0])
            setBannerableId([])
          })
          .catch((error) => {
            console.error('Error saving banner:', error)
            toast.error("Errore durante l'inserimento del banner")
          })
      }
    },
    [dispatch, isValid, slot, bannerableType, bannerableId]
  )

  const getOptions = useCallback(
    (arr: CustomBanner[] | null | undefined): BannerOption[] => {
      const result: BannerOption[] = []
      if (!arr) return result
      if (arr.length === 0) return result
      arr.forEach((elem) => {
        const obj: BannerOption = {
          value: String(elem.id),
          label: `${elem.id} ${elem.destination ?? ''}`,
        }
        result.push(obj)
      })
      return result
    },
    []
  )

  const handleSelectChange = useCallback(
    (selectedOptions: MultiValue<BannerOption> | null) => {
      if (!selectedOptions) {
        setBannerableId([])
      } else {
        const arr = selectedOptions.map((option) => Number(option.value))
        setBannerableId(arr)
      }
    },
    []
  )

  const handleBannerableTypeChange = useCallback(
    (selectedOption: SingleValue<BannerableTypeOption> | null) => {
      if (selectedOption) {
        setBannerableType(selectedOption)
        setBannerableId([])
      }
    },
    []
  )

  const renderContent = useCallback(() => {
    const options = getOptions(customBannersState.allBanners)
    return (
      <form
        className='flex h-full flex-col justify-between gap-3'
        onSubmit={handleSave}
      >
        <div>
          <div>
            <label className='block text-sm font-medium text-gray-700'>
              Slot
            </label>
            <Input
              type='text'
              value={slot}
              onChange={(event) => setSlot(event.target.value)}
              className='h-8 w-[150px] lg:w-[250px]'
            />
          </div>
          <div className='my-3'>
            <label className='block text-sm font-medium text-gray-700'>
              Tipo
            </label>
            <Select
              options={BANNERABLE_TYPES}
              value={bannerableType}
              onChange={handleBannerableTypeChange}
              className='h-8 w-[150px] lg:w-[250px]'
              classNamePrefix='select'
            />
          </div>
          <label className='block text-sm font-medium text-gray-700'>
            Banner
          </label>
          <Select
            isMulti
            name='selectedCategories'
            options={options}
            className='h-8 w-full'
            classNamePrefix='select'
            placeholder='Seleziona i Banner'
            onChange={handleSelectChange}
          />
        </div>

        <div className='mt-10 self-end'>
          <Button disabled={!isValid()} onClick={handleSave} type='submit'>
            Salva
          </Button>
        </div>
      </form>
    )
  }, [
    slot,
    bannerableType,
    handleSave,
    isValid,
    getOptions,
    handleSelectChange,
    customBannersState.allBanners,
    handleBannerableTypeChange,
  ])

  const content = customBannersState.loading ? (
    <Loader className='h-5 w-5 animate-spin' />
  ) : (
    renderContent()
  )

  return <div className={classNames('banner-form', className)}>{content}</div>
}

export default BannerForm
